import React, { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Box } from '@mui/material';
import _ from 'lodash';
import { useClientUIContext } from '../../pages/clients/profile/context/ClientByIdUIContext';
import ClientTabs from '../../pages/clients/profile/tabs/ClientTabs';
import { authService, loginService, makeandcheckerService, usersService } from '../../services';
import { FilterQueryParams, dateFns, getCoreLocale } from '../../helpers';
import ActionDialogNatura from '../../pages/clients/profile/dialogs/ActionDialogNatura';
import statuses from '../../constants/users/statuses';
import tokenStatus from '../../constants/otp/tokenStatus';
import errorCodes from '../../constants/pages/errorCode';
import actionTypes from '../../constants/makeandchecker/actionsTypeId';
import mcStatuses from '../../constants/makeandchecker/statuses';
import forgotTypes from '../../constants/logins/forgotTypes';
import platforms from '../../constants/users/platforms';
import emailTypes from '../../constants/emails/types';
import ClientDropMenu from '../../pages/clients/list/ClientDropMenu';
import { useTranslation } from 'react-i18next';
import CustomAlert from '../../components/Alert';

const ACTIVE_STATUS = 1;

export default function ProfileLayout ({ children }) {
    // Get client Id from url params
    const { t } = useTranslation();
    const { id } = useParams();
    const clientId = parseInt(id);
    // State

    // Client UI Context
    const clientUIContext = useClientUIContext();
    const clientUIProps = useMemo(() => {
        return {
            isLoading: clientUIContext.isLoading,
            setIsLoading: clientUIContext.setIsLoading,
            queryParams: clientUIContext.queryParams,
            setQueryParams: clientUIContext.setQueryParams,
            clientTabData: clientUIContext.clientTabData,
            setClientTabData: clientUIContext.setClientTabData,
        };
    }, [clientUIContext]);
    // Client State
    // const [client, clientUIProps.setClientTabData] = useState();

    // Alert States
    const [alert, setAlert] = useState({
        severity: '',
        title: '',
        description: '',
        className: ''
    });
    const [openAlert, setOpenAlert] = useState(false);

    // Dialogs State
    // eslint-disable-next-line no-unused-vars
    const [showVerifyDialog, setShowVerifyDialog] = useState(false);
    const [showSendPinDialog, setShowSendPinDialog] = useState(false);
    const [showBlockUserDialog, setShowBlockUserDialog] = useState(false);
    const [showRecoverPassDialog, setShowRecoverPassDialog] = useState(false);
    const [actionModal, setActionModal] = useState(null);

    // client discharged status
    const [isDischarged, setIsDischarged] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [reload, setReload] = useState(false);
    const [error, setError] = useState(false);

    // Functions
    const fetchClientById = async () => {
        setIsLoading(true);
        setError(false);

        try {
            const client = await usersService.getById(clientId);

            // Parse the birth date because JS extracts one day
            if (client.person?.attributes?.born_date?.value) {
                const parseBirthDate = dateFns.addDays(new Date(client.person.attributes.born_date.value), 1);
                client.person.attributes.born_date.value = dateFns.format(parseBirthDate, 'yyyy-MM-dd');
            }
            else {
                client.person.attributes.born_date = { value: '' };
            }

            const emails = client.person?.emails || [];
            const telephones = client.person?.telephones || [];
            const isPrincipalEmail = e => (e.id_email_type || e.type?.id) === emailTypes.principal.id;
            const activeEmail = emails.find(e => e.active === ACTIVE_STATUS && isPrincipalEmail(e)) || emails.find(isPrincipalEmail) || emails[0];
            const activeTelephone = telephones.find(t => t.active === ACTIVE_STATUS) || telephones[0];

            const newClientData = {
                ...client,
                person: {
                    ...client.person,
                    email: { description: activeEmail?.description || '-' },
                    telephone: { description: activeTelephone?.description || '-' },
                }
            };
            await clientUIProps.setClientTabData(newClientData);
            setReload(false);
            setIsLoading(false);
        }
        catch (e) {
            setIsLoading(false);
            setError(true);
            setAlert((prevAlert) => ({
                ...prevAlert,
                severity: 'error',
                title: t('errors.generalTitle'),
                description: t('errors.generalDescription'),
                className: 'clientsMC_Alert--error'
            }));
            setOpenAlert(true);
            console.error(e);
        }
    };

    const isClientPending = () => {
        setIsLoading(true);
        const filterMc = new FilterQueryParams();

        filterMc.equal('idType', actionTypes.updateUserStatus);
        filterMc.equal('metadata.path.id', clientId);
        filterMc.equal('id_action_status', mcStatuses.pending.id);

        const option = {
            pageSize: 10000,
            filters: filterMc.build()
        };
        makeandcheckerService
            .getActions(option)
            .then(result => {
                return setIsDischarged(!_.isEmpty(result));
            })
            .then(result => {
                setIsLoading(false);
            })
            .catch(e => {
                setIsLoading(false);
            });
    };

    const prepareTabsData = () => {
        if (clientUIProps?.clientTabData) {
            return {
                id: clientUIProps?.clientTabData?.id,
                avatar: clientUIProps?.clientTabData?.person?.attributes ? `${clientUIProps?.clientTabData?.person?.attributes.first_name?.value[0]}${clientUIProps?.clientTabData?.person?.attributes?.last_name?.value[0]}` : '', // falta agregar el last_name
                status: clientUIProps?.clientTabData?.status.description,
                formattedName: clientUIProps?.clientTabData?.formattedName,
                document: clientUIProps?.clientTabData?.person?.document ? clientUIProps?.clientTabData?.person?.document.description : '-',
                email: clientUIProps?.clientTabData?.person?.email ? clientUIProps?.clientTabData?.person?.email.description : '-',
                occupation: clientUIProps?.clientTabData?.person?.attributes?.economic_activity ? clientUIProps?.clientTabData?.person?.attributes?.economic_activity?.value : '-',
                telephone: clientUIProps?.clientTabData?.person?.telephone ? clientUIProps?.clientTabData?.person?.telephone.description : '-',
            };
        }
    };
        // Handlers

    const sendPinDialog = () => setShowSendPinDialog(true);

    const blockUser = () => {
        setIsLoading(true);
        return usersService
            .toggle(clientUIProps?.clientTabData.id, {
                idStatus: statuses.blocked.id,
                idUpdatedBy: 1,
                reason: 'Modified by operator'
            })
            .then(() => {
                clientUIProps.setQueryParams(prev => ({ ...prev, refresh: !clientUIProps.queryParams.refresh }));
                clientUIProps.setClientTabData({
                    ...clientUIProps?.clientTabData,
                    status: {
                        description: 'blocked',
                        id: statuses.blocked.id
                    }
                });
                setIsLoading(false);
                setAlert((prevAlert) => ({
                    ...prevAlert,
                    severity: 'success',
                    description: t('details.alert.userBlocked'),
                    className: 'clientsMC_Alert--success'
                }));
                setOpenAlert(true);
                setReload(true);
            })
            .catch(e => {
                // change state isLoading
                setIsLoading(false);
                if (e.response?.data?.code !== errorCodes.notAcceptable) {
                    setAlert((prevAlert) => ({
                        ...prevAlert,
                        severity: 'Error',
                        description: t('details.alert.activateError'),
                        className: 'clientsMC_Alert--error'
                    }));
                    setOpenAlert(true);
                    return false;
                }
                setAlert((prevAlert) => ({
                    ...prevAlert,
                    severity: 'error',
                    title: t('errors.generalTitle'),
                    description: t('errors.generalDescription'),
                    className: 'clientsMC_Alert--error'
                }));
                setOpenAlert(true);
                console.error('Error on trying to activate client', e);
            })
            .finally(() => { setShowBlockUserDialog(false); setActionModal(null); });
    };

    const toggleClientStatus = () => {
        // change state isLoading
        setIsLoading(true);

        // Update the status from blocked to recovery
        loginService
            .recover(clientUIProps?.clientTabData.id, platforms.consumer)
            .then(() => {
                if (clientUIProps?.clientTabData.status.description === 'recovery') {
                    setAlert((prevAlert) => ({
                        ...prevAlert,
                        severity: 'success',
                        description: t('details.alert.sendPin'),
                        className: 'clientsMC_Alert--success'
                    }));
                    setOpenAlert(true);
                    setReload(true);
                }
                else {
                    clientUIProps.setClientTabData({
                        ...clientUIProps?.clientTabData,
                        status: {
                            description: 'recovery',
                            id: statuses.recovery.id
                        }
                    });
                    setAlert((prevAlert) => ({
                        ...prevAlert,
                        severity: 'success',
                        description: t('details.alert.pinSuccess'),
                        className: 'clientsMC_Alert--success'
                    }));
                    setOpenAlert(true);
                    setReload(true);
                }
                clientUIProps.setQueryParams(prev => ({ ...prev, refresh: !clientUIProps.queryParams.refresh }));
                setIsLoading(false);
            })
            .catch(e => {
                // change state isLoading
                setIsLoading(false);
                if (e.response?.data?.code !== errorCodes.notAcceptable) {
                    setAlert((prevAlert) => ({
                        ...prevAlert,
                        severity: 'Error',
                        description: t('details.alert.invalidPhone'),
                        className: 'clientsMC_Alert--error'
                    }));
                    setOpenAlert(true);
                    return false;
                }
                setAlert((prevAlert) => ({
                    ...prevAlert,
                    severity: 'error',
                    title: t('errors.generalTitle'),
                    description: t('errors.generalDescription'),
                    className: 'clientsMC_Alert--error'
                }));
                setOpenAlert(true);
                console.error('Error on lock reply client', e);
            })
            .finally(() => { setShowSendPinDialog(false); setActionModal(null); });
    };

    const verifyUserOTP = (data) => {
        setIsLoading(true);
        const boton = _.filter(document.querySelectorAll('.rs-btn.rs-btn-primary'), b => b.innerText === 'Aceptar');
        boton[0].addEventListener('click', function () {
            const body = {
                token: data,
                userDni: clientUIProps?.clientTabData.person.document?.description
            };
            authService.verifyOTP(body)
                .then(res => {
                    setShowVerifyDialog(false);
                    setIsLoading(false);
                    if (res && res.id_token_status === tokenStatus.activated.id) {
                        setAlert((prevAlert) => ({
                            ...prevAlert,
                            severity: 'success',
                            description: t('details.alert.validationSuccess'),
                            className: 'clientsMC_Alert--success'
                        }));
                        setOpenAlert(true);
                        setReload(true);
                    }
                })
                .catch(e => {
                    setShowVerifyDialog(false);
                    setAlert((prevAlert) => ({
                        ...prevAlert,
                        severity: 'error',
                        title: t('errors.generalTitle'),
                        description: t('errors.generalDescription'),
                        className: 'clientsMC_Alert--error'
                    }));
                    setOpenAlert(true);
                    setIsLoading(false);
                    console.error('Error in the validate of the OTP', e);
                });
        });
    };

    const recoverPassClient = () => {
        setIsLoading(true);
        loginService
            .forgot({
                document: clientUIProps?.clientTabData.person.document.description,
                idForgotType: forgotTypes.token.id,
                idPlatform: platforms.consumer,
                metadata: {
                    locale: getCoreLocale()
                }
            })
            .then(() => {
                clientUIProps.setQueryParams(prev => ({ ...prev, refresh: !clientUIProps.queryParams.refresh }));
                setIsLoading(false);
                setAlert((prevAlert) => ({
                    ...prevAlert,
                    severity: 'success',
                    description: t('details.alert.mailSuccess'),
                    className: 'clientsMC_Alert--success'
                }));
                setOpenAlert(true);
                setReload(true);
            })
            .catch(e => {
                setIsLoading(false);
                if (e.response?.data?.code !== errorCodes.notAcceptable) {
                    setAlert((prevAlert) => ({
                        ...prevAlert,
                        severity: 'Error',
                        description: t('details.alert.invalidMail'),
                        className: 'clientsMC_Alert--error'
                    }));
                    setOpenAlert(true);
                    return false;
                }
                setAlert((prevAlert) => ({
                    ...prevAlert,
                    severity: 'error',
                    title: t('errors.generalTitle'),
                    description: t('errors.generalDescription'),
                    className: 'clientsMC_Alert--error'
                }));
                setOpenAlert(true);
                console.error('Error on lock reply client', e);
            })
            .finally(() => { setShowRecoverPassDialog(false); setActionModal(null); });
    };

    // Handle query params changes. On any param change, fetch comments.
    useEffect(() => {
        if (clientId !== clientUIProps?.clientTabData?.id || reload) {
            isClientPending();
            fetchClientById();
        }
        // eslint-disable-next-line
    }, [clientId, reload]);

    // Main Component

    return (
        <>
            <CustomAlert
                openAlert={openAlert}
                setOpenAlert={setOpenAlert}
                alert={alert}
                setAlert={setAlert}
            />

            <ActionDialogNatura
                showSendPinDialog={showSendPinDialog}
                showBlockUserDialog={showBlockUserDialog}
                showRecoverPassDialog={showRecoverPassDialog}
                onHide={() => { setShowBlockUserDialog(false); setShowRecoverPassDialog(false); setShowSendPinDialog(false); }}
                data={prepareTabsData()}
                toggleClientStatus={toggleClientStatus}
                blockUser={blockUser}
                recoverPass={recoverPassClient}
                actionModal={actionModal}
                verifyUser={verifyUserOTP}
                clientUIProps={clientUIProps}
            />
            <Box
                display='flex'
                flexDirection='column'
                alignItems='center'
                width='99%'
            >
                <ClientTabs
                    data={prepareTabsData()}
                    clientId={clientId}
                    sendPin={sendPinDialog}
                    setShowBlockUserDialog={setShowBlockUserDialog}
                    setShowRecoverPassDialog={setShowRecoverPassDialog}
                    setActionModal={setActionModal}
                    client={clientUIProps.clientTabData}
                    setAlert={setAlert}
                    isDischarged={isDischarged}
                    setOpenAlert={setOpenAlert}
                    isLoading={isLoading}
                    error={error}
                />
                <Box
                    className='menuFlex'
                    width='101%'
                >
                    <Box className='menuFlexLeft'>
                        <ClientDropMenu data={prepareTabsData()}/>
                    </Box>
                    <Box className='menuFlexRight'>
                        {children}
                    </Box>
                </Box>
            </Box>
        </>
    );
}
